import { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { gainIcon } from '../utils/gainIcon';
import { shortExerciseName } from '../utils/shortName';

function useCountUp(target, active, duration = 1100, delay = 0, from = 0) {
  const [value, setValue] = useState(from);
  const rafRef = useRef(null);

  useEffect(() => {
    const to = Number(target) || 0;
    if (!active) { setValue(to); return; }
    let start = null;
    const tick = (now) => {
      if (start === null) start = now + delay;
      if (now < start) { rafRef.current = requestAnimationFrame(tick); return; }
      const t = Math.min(1, (now - start) / duration);
      setValue(from + (to - from) * (1 - Math.pow(1 - t, 3)));
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
  }, [target, active, duration, delay, from]);

  return value;
}

/** 15.0 -> "15", 12.46 -> "12.5" */
const trim = (v) => (Math.round(v * 10) / 10).toFixed(1).replace(/\.0$/, '');

/** One strength-gain row: slides in and rolls its gain up from zero. */
export function StrengthGainRow({ gain, index, testId }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const delay = index * 120;
  const animGain = useCountUp(gain.gain_kg, inView, 1100, delay);
  const animCurrent = useCountUp(gain.current_weight, inView, 1100, delay, gain.first_weight);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: -14 }}
      animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -14 }}
      transition={{ delay: index * 0.12, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="flex items-center gap-3"
      data-testid={testId}
    >
      <img
        src={gainIcon(gain.equipment, gain.exercise)}
        alt=""
        width={48}
        height={48}
        loading="lazy"
        decoding="async"
        className="w-12 h-12 rounded-xl shrink-0 object-cover"
        data-testid={`strength-gain-icon-${index}`}
      />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-heading font-bold text-white truncate" title={gain.exercise}>
          {shortExerciseName(gain.exercise)}
        </p>
        <p className="text-xs text-white/40">{gain.sessions} sessions</p>
      </div>
      <div className="text-right shrink-0">
        <p className="text-base font-heading font-black text-accent tabular-nums">
          +{trim(animGain)}kg
        </p>
        <p className="text-xs text-white/40 tabular-nums">
          {gain.first_weight}kg → {trim(animCurrent)}kg
        </p>
      </div>
    </motion.div>
  );
}
